import React, { useEffect, useState } from "react";
import axios, { CancelToken } from "axios";
import { useTranslation } from "react-i18next";
import { isEqualWith, isNull } from "lodash";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import useModal from "hooks/modals/useModal";

import { StatusCode } from "api/protocols";

import { GetCountry } from "api/rpc/2022-09/common/country/country";

import {
  GetCustomerTag,
  PutCustomer,
  GetCustomerAll,
  PostExistingCustomer,
  DeleteCustomer,
  PutCustomerAddressLine,
  PostCustomerAddressLine,
  DeleteCustomerAddressLine,
  GetCustomerNotes,
} from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { GetTag } from "api/rpc/2024-04/facilityAdmin/client/tags";

import { showError, showSuccess } from "redux/actions/ui";
import { IAddress, ICustomer } from "redux/reducers/models/customer";

import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import { unsavedChangesExist } from "helpers/Helpers";

import { useCustomerContext } from "../../context/CustomerContext";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import { Select } from "components/select/index";
import { ButtonNew as Button } from "components/buttonNew";
import { Badge } from "components/badge/Badge";
import Popup from "components/popup/Popup";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import Page from "components/page/Page";
import Card from "components/card/Card";
import TagSelect from "components/tagSelect/TagSelect";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import SelectNew from "components/select/SelectNew";
import CustomerAddressModal, { IAddressLineState } from "elements/customer/CustomerAddressModal";
import ExistingCustomers from "components/existingCustomers/ExistingCustomers";
import Portal from "elements/Portal";

import "./CustomerProfile.scss";
import { GetCustomerType } from "api/rpc/2024-04/facilityAdmin/customer/type";

interface ICustomerTagState {
  tags: any[];
  attachedTags: any[];
  fullTagList: any[];
}

export default function CustomerProfile() {
  const history = useHistory();
  const { t } = useTranslation();
  const { Option } = Select;
  const { facilityStore, authStore } = useAppSelector(store => store);

  const windowSize = useWindowSize();

  const dispatch = useAppDispatch();

  const { state, updateState, loadCustomer } = useCustomerContext(); // changes seen across customer context

  const [deleteCustomerConfirmation, setDeleteCustomerConfirmation] = useState(false);
  const [deleteAddressConfirmation, setDeleteAddressConfirmation] = useState({
    visible: false,
    address: null,
  });

  const [addressModalOpen, setAddressModalOpen] = useState(false);

  const [customerProfileStateBeforeChanges, setCustomerProfileStateBeforeChanges] = useState<ICustomer>(state.customer);

  const [customerTagState, setCustomerTagState] = useState<ICustomerTagState>({
    tags: [],
    attachedTags: [],
    fullTagList: [],
  });

  const { state: existingCustomerModal, updateModal: updateExistingCustomerModal, closeModal: closeExistingCustomerModal } = useModal({
    existingCustomers: [] as ICustomer[],
    popupText: "" as string,
  });

  // Populate Profile Dropdowns
  useEffect(() => {
    const source = axios.CancelToken.source();

    if (state.countries === null) {
      void GetCountries(source.token);
    }

    if (state.customerTypes === null) {
      void GetCustomerTypes(source.token);
    }

    if (state.cartTypes === null) {
      void GetCustomerCartTypes(source.token);
    }

    if (state.notes === null) {
      void loadCustomerNotes(state.customer.id);
    }

    // Reset inputs when tab changes
    if (JSON.stringify(state.customer) != JSON.stringify(state.editState)) {
      updateState({ editState: { ...state.customer } });
    }

    return () => {
      source.cancel();
    };
  }, [state.customer.id]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadTags(source.token);
    return () => source.cancel();
  }, []);

  useEffect(() => {
    setCustomerProfileStateBeforeChanges({ ...state?.customer });
  }, [state?.customer?.banned, state?.customer?.banned_reason]);

  async function loadTags(token: CancelToken) {
    const getTagRes = await GetTag({ type: "customer" }, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (getTagRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.customer.tabs.profile.Customer_Profile.075")));
      return;
    }

    const fullTagList = getTagRes.data.slice();

    const params = {
      customer_id: state.customer.id,
    };

    const getCustomerTagRes = await GetCustomerTag(params, token ? false : true, token);
    if (token && token.reason) {
      return;
    }
    if (getCustomerTagRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.customer.tabs.profile.Customer_Profile.076")));
      return;
    }

    //filter attached tags from tag list
    for (let i = 0; i < getTagRes.data.length; i++) {
      getTagRes.data[i].checked = false;

      for (let k = 0; k < getCustomerTagRes.data.length; k++) {
        if (getTagRes.data[i].id === getCustomerTagRes.data[k].id) {
          getTagRes.data.splice(i, 1);
        }
      }
    }

    setCustomerTagState(prevState => ({
      ...prevState,
      tags: getTagRes.data,
      attachedTags: getCustomerTagRes.data,
      fullTagList: fullTagList,
    }));
  }

  // Save EditState and update context state
  async function saveCustomer() {
    const customerRes = await PutCustomer(
      {
        id: state.editState.id,
        first_name: state.editState.first_name,
        last_name: state.editState.last_name,
        email: state.editState.email,
        phone: state.editState.phone,
        customer_type_id: state.editState.customer_type_id,
        cart_customer_type_id: state.editState.cart_customer_type_id,
        member_code: state.editState.member_code,
        username: state.editState.username,
        birth_date: state.editState.birth_date,
        bag_number: state.editState.bag_number,
        home_facility_id: state.editState.home_facility_id,
      },
      true,
    );

    // Handle if customer already exists
    if (customerRes.status === StatusCode.BAD_REQUEST) {
      const message = customerRes.data;

      const existingCustomerRes = await GetCustomerAll(
        {
          email: message.includes("phone") ? undefined : state.editState.email,
          phone: message.includes("phone") ? state.editState.phone : undefined,
          full_name: message.includes("phone")
            ? `${state.editState.first_name} ${state.editState.last_name}`
            : undefined,
        },
        true,
      );

      if (existingCustomerRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.customer.tabs.profile.Customer_Profile.077")));
        return;
      }

      const customers = existingCustomerRes.data.filter((customer: ICustomer) => customer.id !== state.customer.id);

      updateState({ editState: { ...state.editState, ...customerProfileStateBeforeChanges } });
      updateExistingCustomerModal({
        isOpen: true,
        existingCustomers: customers,
      });

      return;
    }

    if (
      !isEqualWith(state.customer?.address_lines, state.editState?.address_lines, (originalValue, newValue) => {
        if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
          return true;
        }
      }) &&
      state.editState
    ) {
      for (const addressLine of state.editState.address_lines) {
        const addressLineRes = await PutCustomerAddressLine(
          {
            id: addressLine?.id,
            customer_id: state.customer?.id,
            country_id: addressLine?.country_id,
            province_id: addressLine?.province_id,
            city: addressLine?.city,
            default: addressLine?.default,
            address_line_1: addressLine?.address_line_1,
            address_line_2: addressLine?.address_line_2,
            postal: addressLine?.postal,
            phone: addressLine?.phone,
          },
          true,
        );
        if (addressLineRes.status !== StatusCode.OK) {
          dispatch(showError(t("secure.facility.customer.tabs.profile.Customer_Profile.047")));
          return;
        }
      }
    }

    if (customerRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.customer.tabs.profile.Customer_Profile.048")));
      return;
    }

    dispatch(showSuccess(t("secure.facility.customer.tabs.profile.Customer_Profile.049")));

    updateState({
      customer: { ...state.customer, ...customerRes.data },
      editState: { ...state.editState, ...customerRes.data },
    });

    setCustomerProfileStateBeforeChanges({ ...state.editState, ...(customerRes.data as ICustomer) });
  }

  /** Called after POPUP to delete the customer and navigate to /admin/customers */
  async function deleteCustomer(customerId: number) {
    const deleteRes = await DeleteCustomer({ id: customerId }, true);

    if (deleteRes.status !== StatusCode.OK) {
      dispatch(showSuccess(t("secure.facility.customer.tabs.profile.Customer_Profile.050")));
      return;
    }

    setDeleteCustomerConfirmation(false);
    dispatch(showSuccess(t("secure.facility.customer.tabs.profile.Customer_Profile.051")));
    history.push("/admin/customers");
  }

  async function loadExistingCustomer(customerId: number) {
    if (!customerId) {
      return;
    }

    const customerRes = await PostExistingCustomer({ customer_id: customerId }, true);

    if (customerRes?.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.customer.tabs.profile.Customer_Profile.078")));
      return;
    }

    dispatch(showSuccess(t("secure.facility.customer.tabs.profile.Customer_Profile.079")));
    await loadCustomer(customerRes.data?.id as number);
  }

  async function GetCountries(token: CancelToken) {
    const countryRes = await GetCountry({ provinces: true }, false, token);

    if (token.reason) {
      return;
    }

    if (countryRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.customer.tabs.profile.Customer_Profile.052")));
      return;
    }

    updateState({ countries: countryRes.data });
  }

  async function GetCustomerTypes(token: CancelToken) {
    const typesRes = await GetCustomerType(
      {
        application: "green_fee",
      },
      false,
      token,
    );

    if (token.reason) {
      return;
    }

    if (typesRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.customer.tabs.profile.Customer_Profile.053")));
      return;
    }

    console.log("customerTypesfunc", typesRes);
    updateState({ customerTypes: typesRes.data });
  }

  async function GetCustomerCartTypes(token: CancelToken) {
    const typesRes = await GetCustomerType(
      {
        application: "power_cart",
      },
      token ? false : true,
      token,
    );

    if (token.reason) {
      return;
    }

    if (typesRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.customer.tabs.profile.Customer_Profile.054")));
      return;
    }

    console.log("cart types", typesRes.data);
    updateState({ cartTypes: typesRes.data });
  }

  async function addAddressLine(addressLineState: IAddressLineState) {
    const addressLineRes = await PostCustomerAddressLine(
      {
        customer_id: state.customer.id,
        country_id: addressLineState?.country_id,
        province_id: addressLineState?.province_id,
        city: addressLineState?.city,
        default: addressLineState?.default,
        address_line_1: addressLineState?.addressLine1,
        address_line_2: addressLineState?.addressLine2,
        postal: addressLineState?.postal,
        phone: addressLineState?.phone,
      },
      true,
    );
    if (addressLineRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.customer.tabs.profile.Customer_Profile.055")));
      return;
    }

    dispatch(showSuccess(t("secure.facility.customer.tabs.profile.Customer_Profile.056")));

    updateState({ editState: { ...state.editState, ...addressLineRes.data } });
    setAddressModalOpen(false);
  }

  async function handleDefaultAddress(addressLine: IAddress) {
    const addressLineRes = await PutCustomerAddressLine(
      {
        id: addressLine?.id,
        customer_id: state.customer?.id,
        country_id: addressLine?.country_id,
        province_id: addressLine?.province_id,
        city: addressLine?.city,
        default: !addressLine?.default,
        address_line_1: addressLine?.address_line_1,
        address_line_2: addressLine?.address_line_2,
        postal: addressLine?.postal,
        phone: addressLine?.phone,
      },
      true,
    );
    if (addressLineRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.customer.tabs.profile.Customer_Profile.057")));
      return;
    }

    updateState({
      customer: { ...state.customer, ...addressLineRes.data },
      editState: { ...state.editState, ...addressLineRes.data },
    }); // Update UI
  }

  async function deleteCustomerAddress(addressLine: IAddress) {
    const res = await DeleteCustomerAddressLine(
      { customer_id: state.customer.id, address_line_id: addressLine.id },
      true,
    );


    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.customer.tabs.profile.Customer_Profile.058")));
      return;
    }

    updateState({
      customer: { ...state.customer, ...res.data },
      editState: { ...state.editState, ...res.data },
    });

    dispatch(showSuccess(t("secure.facility.customer.tabs.profile.Customer_Profile.059")));
    setDeleteAddressConfirmation({ visible: false, address: null });
  }

  function addressLineInputHandler(event: React.ChangeEvent<HTMLInputElement>, index: number, id: string) {
    const { value } = event.target;
    const updatedAddressLines = state.editState.address_lines ? [...state.editState.address_lines] : [];
    updatedAddressLines[index] = { ...updatedAddressLines[index], [id]: value };

    updateState({
      editState: {
        ...state.editState,
        address_lines: updatedAddressLines,
      },
    });
  }

  function addressLineDropdownChange(value: number, id: string, index: number) {
    const updatedAddressLines = state.editState.address_lines ? [...state.editState.address_lines] : [];
    updatedAddressLines[index] = { ...updatedAddressLines[index], [id]: value };

    updateState({
      editState: {
        ...state.editState,
        address_lines: updatedAddressLines,
      },
    });
  }

  function inputHandler(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = event.target;
    updateState({ editState: { ...state.editState, [id]: value } });
  }

  function handleHomeCourseSelect(selected_home_facility_id: number) {
    const updatedState = {
      ...state.editState,
      home_facility_id: selected_home_facility_id,
    };

    updateState({ editState: updatedState });
  }

  function renderSaveChanges(screenSize: "desktop" | "mobile") {
    return (
      <Button
        type="primary"
        onClick={() => void saveCustomer()}
        className={`customer-profile-save-changes-${screenSize}`}
      >
        {t("secure.facility.customer.tabs.profile.Customer_Profile.002")}
      </Button>
    );
  }

  function renderAddAddress(screenSize: "desktop" | "mobile") {
    const isDesktop = windowSize.width > MOBILE_WIDTH;

    const addAddress = (
      <Button
        className={`customer-profile-action customer-profile-add-address-${screenSize}`}
        size="medium"
        type="link"
        onClick={() => setAddressModalOpen(true)}
      >
        {t("secure.facility.customer.tabs.profile.Customer_Profile.028")}
      </Button>
    );

    if (screenSize === "desktop" && isDesktop) {
      return <>{addAddress}</>;
    } else if (screenSize === "mobile" && !isDesktop) {
      return <FormLayout.Group>{addAddress}</FormLayout.Group>;
    } else {
      return <></>;
    }
  }

  function handleDateChange(selectedDate: Date) {
    const updatedState = {
      ...state.editState,
      birth_date: selectedDate,
    };

    updateState({ editState: updatedState });
  }

  async function loadCustomerNotes(customerId: number, globalLoader?: boolean, token?: CancelToken) {
    const customerNotesRes = await GetCustomerNotes(
      {
        customer_id: customerId,
      },
      globalLoader,
      token,
    );

    if (customerNotesRes.status !== StatusCode.OK) {
      return;
    }

    console.log("cust notes", customerNotesRes.data); //debug

    updateState({ notes: customerNotesRes.data });
  }

  return (
    <>
      <Page
        splitLayout
        notificationBarProps={{
          isVisible: unsavedChangesExist(customerProfileStateBeforeChanges, state.editState),
          onAction: () => void saveCustomer(),
          onCancel: () => updateState({ editState: { ...state.editState, ...customerProfileStateBeforeChanges } }),
        }}
      >
        <Page.Section twoThirds>
          <div className="mb-5">
            <span className="flex mt-2">
              <h1 className="ui-customer_page-header">
                {t("secure.facility.customer.tabs.profile.Customer_Profile.001")}
              </h1>

              <div>
                <NavigationDropdownNew
                  label={t("secure.facility.customer.tabs.profile.Customer_Profile.080")}
                  rightAlign
                  sections={[
                    [
                      {
                        icon: "plus",
                        label: t("secure.facility.customer.tabs.profile.Customer_Profile.081"),
                        onClick: () => setAddressModalOpen(true),
                      },
                    ],
                  ]}
                />
              </div>

              {/* {renderCustomerPreferences("desktop")} */}
              {/* {renderSaveChanges("desktop")} */}
            </span>
            <hr className="customer-profile-header-divider" />
            <Form>
              <FormLayout>
                <FormLayout.Group>
                  <Input
                    value={state.editState.first_name || ""}
                    label={t("secure.facility.customer.tabs.profile.Customer_Profile.003")}
                    id="first_name"
                    onChange={inputHandler}
                  />
                  <Input
                    value={state.editState.last_name || ""}
                    label={t("secure.facility.customer.tabs.profile.Customer_Profile.004")}
                    id="last_name"
                    onChange={inputHandler}
                  />
                </FormLayout.Group>

                <FormLayout.Group>
                  <Input
                    value={state.editState.phone || ""}
                    label={t("secure.facility.customer.tabs.profile.Customer_Profile.005")}
                    id="phone"
                    onChange={inputHandler}
                  />
                  <Input
                    value={state.editState.email || ""}
                    label={t("secure.facility.customer.tabs.profile.Customer_Profile.006")}
                    id="email"
                    onChange={inputHandler}
                  />
                </FormLayout.Group>

                <div className="customer-profile-mobile-adaptive-fields">
                  <Input
                    value={state.editState?.username || ""}
                    label={t("secure.facility.customer.tabs.profile.Customer_Profile.040")}
                    id="username"
                    onChange={inputHandler}
                  />
                  <div className="password-input">
                    <Input
                      value={"**********"}
                      type="password"
                      autoComplete="off"
                      label={t("secure.facility.customer.tabs.profile.Customer_Profile.041")}
                      disabled={true}
                      className="password-input-input"
                    />
                  </div>
                </div>

                {renderAddAddress("mobile")}

                <div className="customer-profile-mobile-adaptive-fields-triple">
                  <SelectNew
                    id={"customer_type_id"}
                    value={state.editState.customer_type_id}
                    onChange={value => updateState({ editState: { ...state.editState, customer_type_id: value } })}
                    label={t("secure.facility.customer.tabs.profile.Customer_Profile.010")}
                    isLoading={state.customerTypes === null}
                    options={state.customerTypes?.map(type => ({
                      id: type.id,
                      label: type.full_title,
                    }))}
                    searchable
                    placeholder={t("secure.facility.customer.tabs.profile.Customer_Profile.082")}
                    disabled={!authStore?.user?.permissions?.user_edit_customer_type}
                    // onClear={() => updateState({ editState: { ...state.editState, customer_type_id: undefined}})}
                  />

                  <SelectNew
                    id={"customer_cart_type"}
                    value={state.editState.cart_customer_type_id}
                    onChange={value => updateState({ editState: { ...state.editState, cart_customer_type_id: value } })}
                    label={t("secure.facility.customer.tabs.profile.Customer_Profile.014")}
                    isLoading={state.cartTypes === null}
                    options={state.cartTypes?.map(type => ({
                      id: type.id,
                      label: type.full_title,
                    }))}
                    searchable
                    placeholder={t("secure.facility.customer.tabs.profile.Customer_Profile.083")}
                    disabled={!authStore?.user?.permissions?.user_edit_customer_type}
                    // onClear={() => updateState({ editState: { ...state.editState, cart_customer_type_id: undefined}})}
                  />

                  {state.editState?.birth_date ? (
                    <div className="birthday-input">
                      <DatePickerInput
                        months={1}
                        position={windowSize.width < MOBILE_WIDTH ? "left" : "right"}
                        label={t("secure.facility.customer.tabs.profile.Customer_Profile.070")}
                        startingDate={new Date(state.editState?.birth_date as unknown as string)}
                        setStartingDate={(e: Date) => handleDateChange(e)}
                      />
                      <div className="birthday-cancel">
                        <FontAwesomeIcon
                          className="birthday-cancel-icon"
                          icon={faTimes}
                          onClick={() => updateState({ editState: { ...state.editState, birth_date: null } })}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="birthday-toggle">
                      <span>
                        <Button
                          onClick={() =>
                            updateState({
                              editState: { ...state.editState, birth_date: state.customer.birth_date ?? new Date() },
                            })
                          }
                        >
                          {t("secure.facility.customer.tabs.profile.Customer_Profile.071")}
                        </Button>
                      </span>
                    </div>
                  )}
                </div>

                <FormLayout.Group>
                  <Input
                    value={state.editState.member_code || ""}
                    label={t("secure.facility.customer.tabs.profile.Customer_Profile.015")}
                    id="member_code"
                    onChange={inputHandler}
                    placeholder=""
                  />
                  <Input
                    label={t("secure.facility.customer.tabs.profile.Customer_Profile.016")}
                    id="bag_number"
                    placeholder="513"
                    onChange={inputHandler}
                    value={state.editState.bag_number || ""}
                  />
                  <Input
                    label={t("secure.facility.customer.tabs.profile.Customer_Profile.017")}
                    id="locker_number"
                    placeholder="628"
                  />
                </FormLayout.Group>

                <FormLayout.Group>
                  <Select
                    label={t("Home Course")}
                    defaultValue={state.customer?.home_facility_id === null ? null : state.customer?.home_facility_id}
                    onChange={(selected_course_id: number) => handleHomeCourseSelect(selected_course_id)}
                  >
                    {facilityStore.facilities.map(facility => {
                      return (
                        <Option key={facility.id} value={facility.id} name={facility.full_name}>
                          <span>{facility.long_name}</span>
                        </Option>
                      );
                    })}
                  </Select>
                </FormLayout.Group>
              </FormLayout>
            </Form>

            {state.editState?.address_lines?.map((addressLine, index) => {
              return (
                <React.Fragment key={index}>
                  <div className="customer-default-address-container">
                    <div>
                      <h1 style={{ display: "inline" }} className="ui-customer_page-header">{`Address ${
                        index + 1
                      }`}</h1>
                      {addressLine?.default && (
                        <Badge size="medium" type="gray">
                          {t("secure.facility.customer.tabs.profile.Customer_Profile.018")}
                        </Badge>
                      )}
                    </div>
                    <div className="ui-customer_address-actions">
                      <div>
                        <Button size="medium" type="link" onClick={() => void handleDefaultAddress(addressLine)}>
                          {!addressLine?.default
                            ? t("secure.facility.customer.tabs.profile.Customer_Profile.019")
                            : t("secure.facility.customer.tabs.profile.Customer_Profile.020")}
                        </Button>
                      </div>
                      <Button
                        size="medium"
                        type="link"
                        onClick={() => setDeleteAddressConfirmation({ visible: true, address: addressLine })}
                      >
                        {t("secure.facility.customer.tabs.profile.Customer_Profile.062")}
                      </Button>
                    </div>
                  </div>

                  <Form>
                    <FormLayout>
                      <FormLayout.Group>
                        <Input
                          value={addressLine?.address_line_1 || ""}
                          label={t("secure.facility.customer.tabs.profile.Customer_Profile.021")}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            addressLineInputHandler(event, index, "address_line_1")
                          }
                        />

                        <Input
                          value={addressLine?.address_line_2 || ""}
                          label={t("secure.facility.customer.tabs.profile.Customer_Profile.022")}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            addressLineInputHandler(event, index, "address_line_2")
                          }
                        />
                      </FormLayout.Group>

                      <FormLayout.Group>
                        <Input
                          value={addressLine?.city || ""}
                          label={t("secure.facility.customer.tabs.profile.Customer_Profile.023")}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            addressLineInputHandler(event, index, "city")
                          }
                        />

                        <Input
                          value={addressLine?.postal || ""}
                          label={t("secure.facility.customer.tabs.profile.Customer_Profile.024")}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            addressLineInputHandler(event, index, "postal")
                          }
                        />

                        <Input
                          value={addressLine?.phone || ""}
                          label={t("secure.facility.customer.tabs.profile.Customer_Profile.025")}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            addressLineInputHandler(event, index, "phone")
                          }
                        />
                      </FormLayout.Group>

                      <FormLayout.Group>
                        {state.countries && (
                          <Select
                            label={t("secure.facility.customer.tabs.profile.Customer_Profile.026")}
                            onChange={(value: number) => addressLineDropdownChange(value, "country_id", index)}
                            defaultValue={addressLine?.country_id}
                          >
                            {state?.countries?.map((country, index) => {
                              return (
                                <Option key={index} value={country?.id} name={country?.name}>
                                  {country?.name}
                                </Option>
                              );
                            })}
                          </Select>
                        )}

                        {state?.countries?.some(country => country?.id === addressLine?.country_id) && (
                          <Select
                            label={t("secure.facility.customer.tabs.profile.Customer_Profile.027")}
                            onChange={(value: number) => addressLineDropdownChange(value, "province_id", index)}
                            defaultValue={addressLine?.province_id}
                          >
                            {state.countries
                              ?.find(country => country?.id === addressLine?.country_id)
                              ?.provinces?.map((province: Record<string, any>, index: number) => {
                                return (
                                  <Option key={index} value={province?.id} name={province?.name}>
                                    {province?.name}
                                  </Option>
                                );
                              })}
                          </Select>
                        )}
                      </FormLayout.Group>
                    </FormLayout>
                  </Form>
                </React.Fragment>
              );
            })}

            <div className="customer-profile-actions">{/* <div>{renderAddAddress("desktop")}</div> */}</div>
            <div className="customer-profile-delete-customer">{renderSaveChanges("mobile")}</div>
            <div className="customer-profile-delete-customer">
              <Button type="secondary" onClick={() => setDeleteCustomerConfirmation(true)}>
                {t("secure.facility.customer.tabs.profile.Customer_Profile.063")}
              </Button>
            </div>
          </div>
        </Page.Section>

        <Page.Section oneThird>
          <div className="ml-5">
            <Card>
              <Card.Section>
                <TagSelect
                  label="Tags"
                  tags={customerTagState.tags}
                  attachedTags={customerTagState.attachedTags}
                  fullTagList={customerTagState.fullTagList}
                />
              </Card.Section>
            </Card>
          </div>
        </Page.Section>
      </Page>

      {addressModalOpen && (
        <CustomerAddressModal
          isVisible={addressModalOpen}
          countries={state.countries}
          onCancel={() => setAddressModalOpen(false)}
          onOk={addressLine => void addAddressLine(addressLine)}
        />
      )}

      {/* DELETE CUSTOMER POPUP */}
      <Popup
        open={deleteCustomerConfirmation}
        description={`${t("secure.facility.customer.tabs.profile.Customer_Profile.064")} ${state.customer?.full_name}?`}
        title={t("secure.facility.customer.tabs.profile.Customer_Profile.065")}
        type="warning"
        onCancel={() => setDeleteCustomerConfirmation(false)}
        onOk={() => deleteCustomer(state.customer.id)}
      />

      {/* DELETE ADDRESS POPUP */}
      <Popup
        open={deleteAddressConfirmation.visible}
        description={`${t("secure.facility.customer.tabs.profile.Customer_Profile.066")} ${
          state.customer.full_name
        } ${t("secure.facility.customer.tabs.profile.Customer_Profile.067")}`}
        title={t("secure.facility.customer.tabs.profile.Customer_Profile.068")}
        type="warning"
        onCancel={() => setDeleteAddressConfirmation({ visible: false, address: null })}
        onOk={() => deleteCustomerAddress(deleteAddressConfirmation.address as IAddress)}
      />

      {/* EXISTING CUSTOMERS POPUP */}
      <Portal isMounted={existingCustomerModal.isOpen}>
        <ExistingCustomers
          open={existingCustomerModal.isOpen}
          customers={existingCustomerModal.existingCustomers}
          onClose={closeExistingCustomerModal}
          onLoadCustomer={customerId => void loadExistingCustomer(customerId)}
        />
      </Portal>
    </>
  );
}
